import type { ProtoZoneFinderData } from "@catbento/shared";
import { isGzip } from "../snapshot-loader.js";

// The Zone Finder loader (Phase 02h; reworked in 04m-a). Unlike every other hook this
// fetches a separate file on demand — only when someone clicks "Load event data". Faithful
// to renderers-zonefinder.js's loadZoneFinderData: a SHARED module-level promise so
// several Zone Finder instances (or repeated clicks) trigger at most one fetch, and a
// failed fetch resets it so Retry genuinely re-fetches instead of replaying the rejection.
//
// Two changes from the legacy:
// - It fetches the build's gzipped copy (~2.5MB instead of ~34MB — snapshot-gzip.plugin.ts)
//   and unzips it here, the same way the main snapshot loads (src/snapshot-loader.ts).
// - The result lives in this module, NEVER on window.CatalystData (the legacy wrote
//   `.statsBombOpenData` / `.zoneFinderShotsPasses` onto the global — Ben's MEDIUM risk).
//   `getLoadedZoneFinderData()` is the synchronous "is it loaded?" check.

declare const __ZONE_FINDER_GZ__: ZoneFinderGzMeta | null;

/** The build's info about the .gz — null when the build had no Zone Finder file (PR gate). */
export interface ZoneFinderGzMeta {
  url: string;
  gzBytes: number;
  rawBytes: number;
}

let inFlight: Promise<ProtoZoneFinderData> | null = null;
let loaded: ProtoZoneFinderData | null = null;

/** The data if a load has finished, else null — what a render checks. */
export function getLoadedZoneFinderData(): ProtoZoneFinderData | null {
  return loaded;
}

/** Text of a fetched body — unzipped if it's gzip. If the server ever sends it with
 *  `Content-Encoding: gzip`, the browser has already unzipped it, so unzipping again would
 *  fail; the gzip magic bytes (1f 8b) tell the two apart. */
async function bodyText(bytes: Uint8Array): Promise<string> {
  if (!isGzip(bytes)) return new TextDecoder().decode(bytes);
  // The cast: TS's DOM lib types DecompressionStream's writable side as BufferSource,
  // which a Uint8Array stream is — it just doesn't narrow through pipeThrough.
  const unzipped = new Blob([bytes as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream("gzip") as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
  return new Response(unzipped).text();
}

export function loadZoneFinderData(
  meta: ZoneFinderGzMeta | null = __ZONE_FINDER_GZ__,
  fetchImpl: typeof fetch = fetch,
): Promise<ProtoZoneFinderData> {
  if (loaded) return Promise.resolve(loaded);
  if (inFlight) return inFlight;
  inFlight = (async () => {
    // A rejection, not a throw: the Load button shows its error state and the page lives on.
    if (!meta) throw new Error("No Zone Finder data in this build — was public/js/catalyst-data-zonefinder.json present at build time?");
    const res = await fetchImpl(meta.url);
    if (!res.ok) throw new Error(`Zone Finder data: HTTP ${res.status}`);
    const json = JSON.parse(await bodyText(new Uint8Array(await res.arrayBuffer()))) as Partial<ProtoZoneFinderData>;
    // Missing keys → empty maps, exactly as the legacy (`data.statsBombOpenData || {}`).
    return { statsBombOpenData: json.statsBombOpenData ?? {}, shotsPasses: json.shotsPasses ?? {} };
  })()
    .then((data) => {
      loaded = data;
      return data;
    })
    .catch((err) => {
      inFlight = null; // reset so a retry re-fetches (not a replayed rejection)
      throw err;
    });
  return inFlight;
}

/** Test-only: drop the shared cache so each test starts clean. */
export function _resetZoneFinderCache(): void {
  inFlight = null;
  loaded = null;
}
